import { Accordion } from '@mantine/core';
import { motion } from 'motion/react';
import { FC, useEffect, useMemo, useState } from 'react';

import TimeslotCalendarAccordionItem from '@/components/AppointmentScheduler/TimeslotCalendarAccordionItem.tsx';
import Unavailable from '@/components/Unavailable.tsx';

import { PICKER_INTERACTION_KEY } from '@/constants/availability-telemetry.ts';

import { abortAvailabilityInteraction, settleAvailabilityInteraction } from '@/lib/availability-telemetry.ts';

import { Insurance } from '@/models/appointment/Appointment.ts';
import { EnrichedAppointmentType, Practitioner } from '@/models/appointment/EnrichedPractitionerData.ts';

interface Props {
    selectedAppointment?: EnrichedAppointmentType;
    practitioners: Practitioner[];
    onSlotSelect: (practitioner_id: string, slot: string) => void;
    selectedCalendar?: string;
    selectedSlot?: string;
    insurance: Insurance;
    defaultStartDate?: string;
}

const TimeslotCalendarAccordion: FC<Props> = ({
    selectedAppointment,
    practitioners,
    onSlotSelect,
    selectedCalendar,
    selectedSlot,
    insurance,
    defaultStartDate,
}) => {
    const availablePractitioners = useMemo(
        () =>
            practitioners
                .filter((item) => selectedAppointment?.offered_by_practitioner_ids.includes(item.id))
                .sort((a, b) => {
                    const aHasSelected = a.calendars.some((cal) => cal.pvs.id === selectedCalendar);
                    const bHasSelected = b.calendars.some((cal) => cal.pvs.id === selectedCalendar);

                    return Number(bHasSelected) - Number(aHasSelected);
                }),
        [practitioners, selectedAppointment, selectedCalendar]
    );

    const currentPractitionerIds = availablePractitioners.map((p) => p.id).join(',');
    const [prevPractitionerIds, setPrevPractitionerIds] = useState(currentPractitionerIds);
    const [openedAccordionItem, setOpenedAccordionItem] = useState<string | undefined>(availablePractitioners[0]?.id);

    if (currentPractitionerIds !== prevPractitionerIds) {
        setPrevPractitionerIds(currentPractitionerIds);
        setOpenedAccordionItem(availablePractitioners[0]?.id);
    }

    const selectedComputedSlot = useMemo(() => `${selectedCalendar}_${selectedSlot}`, [selectedCalendar, selectedSlot]);

    useEffect(() => {
        if (availablePractitioners.length > 0) return;

        settleAvailabilityInteraction({
            key: PICKER_INTERACTION_KEY,
            outcome: 'failure',
            reason: 'availability_no_calendar',
        });
    }, [availablePractitioners.length]);

    // The operator navigated away from a check that was still running.
    useEffect(() => () => abortAvailabilityInteraction(PICKER_INTERACTION_KEY), []);

    return (
        <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full overflow-hidden"
        >
            {selectedAppointment ? (
                <Accordion
                    value={openedAccordionItem}
                    onChange={(item) => setOpenedAccordionItem(item as string)}
                    classNames={{ content: 'flex flex-col gap-4 p-0', label: 'py-6' }}
                    className="border-t border-solid"
                >
                    {availablePractitioners.map((practitioner) => (
                        <TimeslotCalendarAccordionItem
                            key={practitioner.id}
                            practitioner={practitioner}
                            selectedCalendar={selectedCalendar}
                            appointmentType={selectedAppointment}
                            insurance={insurance}
                            onSlotSelect={onSlotSelect}
                            selectedSlot={selectedComputedSlot}
                            isOpen={openedAccordionItem === practitioner.id}
                            defaultStartDate={defaultStartDate}
                        />
                    ))}
                </Accordion>
            ) : (
                <Unavailable text="Zeitfenster sind fuer diesen Terminart nicht sichtbar" />
            )}
        </motion.div>
    );
};

export default TimeslotCalendarAccordion;
